import { RegisterLifterCompetitionTypeSelect } from './register-result-competition-type';
import { RegisterLifterAgeCategorySelect } from './register-result-age-category';
import { CompetitionTeamSelect } from '~/pages/competition/teams/components/competition-team-select';
import { GroupSelect } from '~/pages/competition/groups/components/group-select';
import { isBenchpressCompetition } from '~/utils/competition-type';
import { useAuthentication } from '~/hooks/useAuthentication';
import { useCreateResultsMutation } from '../mutations';
import { schema, Schema } from './result-form.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { CompetitionTypeNameEnum } from '~/apis/ssf';
import { Button } from '~/components/Button/Button';
import { Grid } from '~/components/Grid/Grid';
import { Input } from '~/components/Input';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useState } from 'react';

interface Props {
  lifterId: number;
  competitionId?: number;
  isTeamComp?: boolean;
  onFinish?: () => void;
}

export function CompetitionResultForm({ lifterId, competitionId, isTeamComp = false, onFinish }: Props) {
  const { isSuperUser } = useAuthentication();
  const mutationResult = useCreateResultsMutation();
  const [selectedAgeLabel, setSelectedAgeLabel] = useState<string | undefined>();

  const { formState, register, handleSubmit, setValue, watch } = useForm<Schema>({
    defaultValues: {},
    resolver: zodResolver(schema),
  });

  const competitionType = watch('competition_type');
  const isBenchpressCompetitionType = isBenchpressCompetition(
    competitionType?.split('-')[1] as CompetitionTypeNameEnum
  );

  const isU1113Category = selectedAgeLabel === 'U11-13';

  async function onSubmit(data: Schema, id: number) {
    await mutationResult.mutateAsync({
      payload: { ...data, person: id, competition_type: Number(competitionType.split('-')[0]) },
    });
    toast.success('Resultatet har sparats');
    onFinish?.();
  }

  return (
    <form onSubmit={handleSubmit(data => onSubmit(data, lifterId))} autoComplete="off">
      <Grid>
        <Grid.Item cols={{ lg: 2, sm: 4 }}>
          <Input.Group formState={formState} htmlFor="competition_type" label="Tävlingstyp">
            <RegisterLifterCompetitionTypeSelect
              {...register('competition_type')}
              onChange={event => {
                setValue('competition_type', event.target.value);
                if (isBenchpressCompetition(event.target.value.split('-')[1] as CompetitionTypeNameEnum)) {
                  setValue('squat', 0);
                  setValue('deadlift', 0);
                }
              }}
              formState={formState}
              defaultValue={undefined}
            />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ lg: 2, sm: 4 }}>
          <Input.Group formState={formState} htmlFor="age_category" label="Ålderskategori">
            <RegisterLifterAgeCategorySelect
              {...register('age_category')}
              onChange={event => {
                setValue('age_category', Number(event.target.value));
                const selectedOption = event.target.options[event.target.selectedIndex];
                setSelectedAgeLabel(selectedOption?.text);
              }}
              formState={formState}
              defaultValue={undefined}
            />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ lg: 1, sm: 4 }}>
          <Input.Group formState={formState} htmlFor="body_weight" label="Kroppsvikt">
            <Input.Field type="number" step="0.01" {...register('body_weight', { valueAsNumber: true })} min={0} />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ lg: 1, sm: 4 }}>
          <Input.Group formState={formState} htmlFor="squat" label="Knäböj">
            <Input.Field
              type="number"
              step="0.05"
              {...register('squat', { valueAsNumber: true })}
              min={0}
              disabled={isBenchpressCompetitionType}
            />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ lg: 1, sm: 4 }}>
          <Input.Group formState={formState} htmlFor="benchpress" label="Bänkpress">
            <Input.Field type="number" step="0.05" {...register('benchpress', { valueAsNumber: true })} min={0} />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ lg: 1, sm: 4 }}>
          <Input.Group formState={formState} htmlFor="deadlift" label="Marklyft">
            <Input.Field
              type="number"
              step="0.05"
              {...register('deadlift', { valueAsNumber: true })}
              min={0}
              disabled={isBenchpressCompetitionType}
            />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ lg: 1, sm: 4 }}>
          <Input.Group formState={formState} htmlFor="group" label="Grupp">
            <GroupSelect {...register('group')} placeholder={'-'} formState={formState} />
          </Input.Group>
        </Grid.Item>

        {isTeamComp && competitionId && (
          <Grid.Item cols={{ lg: 2, sm: 4 }}>
            <Input.Group formState={formState} htmlFor="team" label="Lag">
              <CompetitionTeamSelect
                competitionId={competitionId}
                {...register('team')}
                value={watch('team') ?? undefined}
                formState={formState}
              />
            </Input.Group>
          </Grid.Item>
        )}

        <Grid.Item cols={{ lg: 1, sm: 4 }}>
          <Input.Group formState={formState} htmlFor="lot" label="Lott">
            <Input.Field type="number" {...register('lot')} />
          </Input.Group>
        </Grid.Item>

        {isSuperUser && (
          <Grid.Item cols={{ lg: 1, sm: 4 }}>
            <Input.Group formState={formState} htmlFor="rank" label="Placering">
              <Input.Field type="number" {...register('rank')} />
            </Input.Group>
          </Grid.Item>
        )}

        <Grid.Item cols={{ lg: 2, sm: 4 }}>
          <Input.InlineGroup formState={formState} htmlFor="is_coef_group" label="Koefficientgrupp">
            <Input.Checkbox {...register('is_coef_group')} formState={formState} />
          </Input.InlineGroup>
          {!isSuperUser && (
            <Input.InlineGroup formState={formState} htmlFor="disqualified" label="Ej godkänd">
              <Input.Checkbox {...register('disqualified')} formState={formState} />
            </Input.InlineGroup>
          )}
        </Grid.Item>

        {isSuperUser && (
          <Grid.Item cols={{ lg: 2, sm: 4 }}>
            <Input.InlineGroup formState={formState} htmlFor="disqualified" label="Ej godkänd">
              <Input.Checkbox {...register('disqualified')} formState={formState} />
            </Input.InlineGroup>
          </Grid.Item>
        )}

        {isU1113Category && (
          <Grid.Item cols={{ lg: 2, sm: 4 }}>
            <Input.Group formState={formState} htmlFor="good_lifts" label="Godkända lyft">
              <Input.Field type="number" {...(register('good_lifts'), { valueAsNumber: true })} />
            </Input.Group>
          </Grid.Item>
        )}

        {isU1113Category && (
          <Grid.Item cols={{ lg: 2, sm: 4 }}>
            <Input.Group formState={formState} htmlFor="good_lifts_bp" label="Varav godkända bänkpressar">
              <Input.Field type="number" {...(register('good_lifts_bp'), { valueAsNumber: true })} />
            </Input.Group>
          </Grid.Item>
        )}
      </Grid>
      <Button.Group align="right">
        <Button type="submit" isLoading={mutationResult.isPending}>
          Spara
        </Button>
      </Button.Group>
    </form>
  );
}
